<template>
  <div class="notifications-component">
    <DialogComponent
      :show="pushNotificationStore.showDialog"
      :title="$t('Notification_Dialog_Title')"
      :text="$t('Notification_Dialog_Text')"
      :accept-text="$t('Notification_Dialog_Change_Programme')"
      :cancel-text="$t('Notification_Dialog_Close')"
      :is-loading="isChangingProgramme"
      @cancel="pushNotificationStore.closeDialog()"
      @close="pushNotificationStore.closeDialog()"
      @accepted="handleDialogAccept"
    />
    <div v-if="appStore.showNotificationCenter === true">
      <!-- Notification Bell -->
      <div class="relative cursor-pointer" @click="handleBellClick">
        <div
          v-if="notificationStore.newCount > 0"
          class="absolute -top-1 right-1 inline-flex select-none items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white"
        >
          <CounterComponent :target-value="0 + notificationStore.newCount" />
        </div>
        <font-awesome-icon :icon="['fal', 'bell']" class="mx-2 text-[30px]" />
      </div>
      <DrawerComponent :title="$t('Notification_Title')" :open="open" @change-open="closePanel">
        <template #headerIcon v-if="Capacitor.isNativePlatform()">
          <font-awesome-icon
            :icon="['fal', 'gear']"
            class="mr-4 h-5 w-5 cursor-pointer text-gray-500"
            @click="openNotificationSettings"
          />
        </template>
        <template #content>
          <template v-if="latestNotifications.length > 0">
            <div
              v-for="notification in latestNotifications"
              :key="notification.id"
              class="flex cursor-pointer items-start gap-3 border-b border-gray-200 p-4"
              :class="{
                'bg-blue-100': notification.isRead === false,
                'bg-white': notification.isRead !== false
              }"
              @click="notificationClicked(notification)"
            >
              <!-- Image / initials -->
              <div
                class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full"
                :class="{
                  'bg-white': notification.isRead === false,
                  'bg-gray-200': notification.isRead !== false
                }"
              >
                <ImageComponent v-bind="getImageProps(notification)" />
              </div>
              <!-- Notification Content -->
              <div class="flex-1">
                <div class="text-sm text-gray-900">
                  <span v-text="notification.body"></span>
                </div>
                <div class="mt-1 flex items-center gap-2 text-xs text-gray-500">
                  <!-- New indicator + text -->
                  <div v-if="notification.isRead === false" class="flex items-center gap-1">
                    <span class="h-2 w-2 rounded-full bg-green-500"></span>
                    <span class="text-gray-700">{{ $t('Notification_New') }}</span>
                  </div>
                  <span>{{ formatNotificationDateTime(notification.created) }}</span>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="flex items-center justify-center p-6 text-sm text-gray-900">
            <span>{{ $t('Notification_No_Results') }}</span>
          </div>
        </template>
      </DrawerComponent>
    </div>
  </div>
</template>
<script setup lang="ts">
import type { NotificationMessage } from '@/generated-api'
import { useNotificationCenterStore } from '@/stores/portal-stores/notification-stores/notificationCenterStore'
import {
  useAppStore,
  useFileStore,
  useProgrammeStore,
  usePushNotificationStore,
  useAccountSettingsStore,
  useDrawerStore,
  useNavigationStore
} from '@/stores'
import { formatDistanceToNow } from 'date-fns'
import { storeToRefs } from 'pinia'
import { formatTimeRange, localeToDateLocale } from '@/application/helpers'
import { Capacitor } from '@capacitor/core'

// Constants and Refs
const fileStore = useFileStore()
const open = ref<boolean>(false)
const appStore = useAppStore()
const notificationStore = useNotificationCenterStore()
const { centerNotifications } = storeToRefs(notificationStore)
const pushNotificationStore = usePushNotificationStore()
const programmeStore = useProgrammeStore()
const brand = appStore.getBrand()
const eventImages = ref<{ userId: string; imageData: string }[]>([])
const imageCache = ref<{ userId: string; imageData: string }[]>([])
const isChangingProgramme = ref(<boolean>false)
const storageLocale = localStorage.getItem('appLanguageLocale') || 'da-DK'
const router = useRouter()
const drawerStore = useDrawerStore()
const accountSettingsStore = useAccountSettingsStore()
const navigationStore = useNavigationStore()

const openNotificationSettings = async () => {
  try {
    //close the notification panel
    closePanel()

    // Find accountsettings from secondary navigation and go to its URL
    const accountSettings = navigationStore.secondaryNavigation.find(
      (item) => item.type === 'accountSettingsLabel'
    )

    if (accountSettings !== undefined && accountSettings.url !== undefined) {
      await router.push(accountSettings.url)
    } else {
      console.error('Account settings URL not found in navigation options.')
      return
    }

    const allSettings = accountSettingsStore.getComputedSettings(false)
    const notificationSettings = allSettings
      .flatMap((section) => section.list) // Flatten all items into one array
      .find((item) => item.sectionType === 'notification')

    if (notificationSettings !== undefined) {
      drawerStore.setActiveDrawer(notificationSettings)
    } else {
      console.error(
        'Notification settings section not found after navigation. Content:',
        appStore.content
      )
    }
  } catch (error) {
    console.error('Error opening notification settings:', error)
  }
}

// Lifecycle Hooks
onMounted(async () => {
  await notificationStore.init()
})

onUnmounted(() => {
  notificationStore.stopPolling()
})

// Methods/Functions
const fetchEventImages = async () => {
  const images: { userId: string; imageData: string }[] = []

  for (const notification of centerNotifications.value) {
    if (notification.senderUserId) {
      const cachedImage = imageCache.value.find((img) => img.userId === notification.senderUserId)

      if (cachedImage) {
        // Use cached image
        images.push({
          userId: notification.senderUserId,
          imageData: cachedImage.imageData
        })
      } else {
        // Fetch image from API
        const response = await fileStore.getUserImage(notification.senderUserId)
        const imageData = response ? 'data:image/jpeg;base64,' + response : ''

        // Store in cache array
        imageCache.value.push({
          userId: notification.senderUserId,
          imageData: imageData
        })

        images.push({
          userId: notification.senderUserId,
          imageData: imageData
        })
      }
    }
  }

  eventImages.value = images
}

const getImageProps = (notification: NotificationMessage) => {
  const imageEntry = notification.senderUserId
    ? eventImages.value.find((img) => img.userId === notification.senderUserId)
    : undefined

  const hasImage = imageEntry && imageEntry.imageData

  return {
    source: hasImage ? imageEntry.imageData : '',
    alt: hasImage ? '' : brand,
    'asset-info': hasImage ? undefined : { name: brand, type: 'logo', extension: 'png' },
    class: ['h-10 w-10', hasImage ? 'object-cover' : 'object-scale-down p-1']
  }
}

const latestNotifications = computed(() => {
  return [...centerNotifications.value].sort((a, b) => b.created!.getTime() - a.created!.getTime())
})

const formatNotificationDateTime = (date: Date | string | undefined): string => {
  if (!date) {
    return ''
  }
  return formatDistanceToNow(date, {
    locale: localeToDateLocale(storageLocale)
  })
}

const handleBellClick = async () => {
  open.value = !open.value
  // Refresh images when opening the panel
  if (open.value) {
    fetchEventImages()
  }
}

const closePanel = () => {
  open.value = false
  notificationStore.markAllAsRead()
}

const notificationClicked = async (notification: NotificationMessage) => {
  console.log('notification', notification)
  await notificationStore.goToLink(notification)
  closePanel()
}

const handleDialogAccept = async () => {
  const notificationMessage = pushNotificationStore.notificationMessage
  if (notificationMessage !== undefined) {
    // Change programmeId
    const programme = programmeStore.programmes.find(
      (prog) => prog.programmeId === notificationMessage.programmeId
    )
    if (programme) {
      // Change to the notification message programme, start a spinner and close the dialog
      isChangingProgramme.value = true
      await programmeStore.selectProgramme(programme)
      pushNotificationStore.closeDialog()
      isChangingProgramme.value = false
    } else {
      console.error('Programme not found')
    }

    // Then go to link in message
    await notificationStore.goToLink(notificationMessage)
  } else {
    console.error('Notification message is undefined.')
  }
}

watch(
  centerNotifications,
  () => {
    if (open.value) {
      fetchEventImages()
    }
  },
  { deep: true }
)
</script>
